import React, { useRef } from 'react';
import { X } from 'lucide-react';
import LayerOverlay from './LayerOverlay';
import useFocusTrap from '../../utils/useFocusTrap';
import './Modal.css';

/* MODAL CANÓNICO de la app. UN solo componente para que todos los diálogos se vean y se comporten igual:
   - fondo oscurecido/translúcido + scroll del cuerpo BLOQUEADO de verdad (vía LayerOverlay → useScrollLock,
     que congela el scroller real —documentElement— y restaura la posición al cerrar);
   - portaleado a <body> (así `position:fixed` cubre el viewport entero aunque un ancestro tenga transform);
   - cierre con Esc y con clic FUERA del contenido;
   - foco ATRAPADO dentro (useFocusTrap) y devuelto al cerrar.
   Chrome estándar: cabecera (título + ✕), cuerpo con scroll interno (máx 88vh) y pie opcional. Los diálogos
   especializados (FichaDialog de la ficha, ConfirmDialog) comparten el MISMO LayerOverlay + useFocusTrap. */
export default function Modal({
  open = true,
  onClose,
  title = null,
  ariaLabel,              // default: el título
  footer = null,
  bloqueado = false,      // guardando: ni el fondo ni la X ni Esc cierran
  ancho = 640,            // ancho objetivo en px (se acota a 94vw)
  className = '',
  children,
}) {
  const ref = useRef(null);
  const cerrar = bloqueado ? null : onClose;
  useFocusTrap(ref, { active: open, onEsc: cerrar });
  if (!open) return null;
  return (
    <LayerOverlay open={open} className="ui-modal-overlay" onBackdrop={cerrar}>
      <div
        ref={ref}
        tabIndex={-1}
        className={`ui-modal${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : 'Diálogo')}
        style={{ width: `min(${Number(ancho) || 640}px, 94vw)` }}
      >
        {title && (
          <header className="ui-modal-head">
            <h3 className="ui-modal-title">{title}</h3>
            <button type="button" className="ui-modal-close" onClick={onClose} aria-label="Cerrar" disabled={bloqueado}>
              <X size={18} aria-hidden="true" />
            </button>
          </header>
        )}
        <div className="ui-modal-body">{children}</div>
        {footer && <footer className="ui-modal-foot">{footer}</footer>}
      </div>
    </LayerOverlay>
  );
}
